import React, {
  useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import {
  AlignLeft, ArrowDown, ArrowUp, BookOpen, ChevronDown, CircleHelp, GalleryHorizontal, Image, LayoutGrid,
  ListOrdered, Mail, Mic, MousePointerClick, PanelBottom, PanelTop, Plus, RectangleHorizontal, Settings, Tags, Trash2, Undo2, X,
} from 'lucide-react';

import {
  createHubItem, createHubSection, loadHubPage, setHubItemOrder, setHubSectionOrder, updateHubItem, updateHubPage,
  updateHubSection,
} from './api';
import {
  CategoryField, IconField, LinkField, LookField, PhotoField, WordField, friendly,
} from './HubFields';
import HubPageLive from './HubPageLive';
import { HubIcon } from './hubIcons';
import { tx } from './i18n';
import {
  Alert, Failed, Loading, Pill,
} from './parts';
import { Switch } from './ProductOptions';
import { SHOP_HOST } from './ProductPageLive';
import {
  ENGLISH, languageLabel, ownWords, saveTexts, unix, wordsOf,
} from './siteWords';
import { errorMessage } from './ui';
import { useLiveResource } from './useLive';

// One Longevity Hub page, written.
//
// A page is its sections, top to bottom, each of a kind (XOmics says which
// kinds there are and what each has: hub.kinds). A section is a card here:
// its words, its photo, where its button leads, and for a section that lists
// things (cards, points, questions) its entries. Sections and entries are
// added, moved, switched off and removed at once; words are typed and then
// saved together (the bar at the bottom), through the translation endpoints,
// in the language chosen above. What there is, its order and its photos are
// the same in every language. The page beside the cards is the store's own
// drawing of what is here, saved or not (HubPageLive.js).

const KIND_ICON = Object.freeze({
  cover: PanelTop, guides: GalleryHorizontal, topics: Tags, latest: LayoutGrid, podcast: Mic, newsletter: Mail,
  hero: PanelTop, contents: ListOrdered, text: AlignLeft, picture: Image, band: RectangleHorizontal,
  cta: MousePointerClick, questions: CircleHelp, sources: BookOpen, end: PanelBottom,
});
// The words a card is known by in the list, the first of them that is written.
const NAMED_BY = Object.freeze({
  cover: ['lead', 'title'], hero: ['lead', 'title'], contents: ['kicker'], picture: ['alt'], band: ['alt'], end: ['lead'],
  card: ['title'], read: ['title'], chip: ['label'], listen: ['label'], text: ['text', 'heading'], point: ['text'],
  question: ['question'],
});
// The photos the store crops to their frame, where a person says what must stay in view.
const CROPPED = new Set(['cover', 'card', 'read', 'listen', 'hero']);

const keysOf = (words) => Object.fromEntries(Object.entries(words).map(([field, text]) => [field, text.key]));
const clip = (value, length = 90) => (value.length > length ? `${value.slice(0, length - 1)}…` : value);

function HubPageEditor({
  pageId, hub, language, languages, canManage, siteTextId, products, onListChanged,
}, ref) {
  const one = useLiveResource(['translations'], useCallback(() => loadHubPage(pageId, language), [pageId, language]));
  const page = one.data?.page ?? null;
  const [drafts, setDrafts] = useState({});
  const [openId, setOpenId] = useState(null);
  const [focus, setFocus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [adding, setAdding] = useState(false);
  const [settings, setSettings] = useState(false);
  const [undo, setUndo] = useState(null);
  const cards = useRef(null);
  useEffect(() => { setDrafts({}); setNotice(null); setUndo(null); setAdding(false); }, [pageId, language]);
  useEffect(() => { setOpenId(null); setSettings(false); }, [pageId]);

  const other = language !== ENGLISH;
  const kinds = hub.kinds;
  const write = canManage && !busy;

  // Every text of the page by its key, and what stands in each box now.
  const texts = useMemo(() => {
    const byKey = new Map();
    if (!page) return byKey;
    const take = (words, field) => Object.entries(words).forEach(([name, text]) => byKey.set(text.key, { text, field: field || name }));
    take(page.words);
    for (const section of page.sections) {
      take(section.words);
      take(section.defaults, 'shared');
      section.items.forEach((item) => take(item.words));
    }
    return byKey;
  }, [page]);
  const stored = useCallback((text, field) => friendly(ownWords(text, language), field), [language]);
  const valueOf = useCallback((text, field) => (drafts[text.key] !== undefined ? drafts[text.key] : stored(text, field)),
    [drafts, stored]);
  const dirty = useMemo(() => Object.keys(drafts).filter((key) => {
    const entry = texts.get(key);
    return entry && unix(drafts[key]) !== stored(entry.text, entry.field);
  }), [drafts, stored, texts]);
  const type = useCallback((text, value) => setDrafts((current) => ({ ...current, [text.key]: value })), []);

  useImperativeHandle(ref, () => ({ isDirty: () => dirty.length > 0 }), [dirty]);
  useEffect(() => {
    if (dirty.length === 0) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty.length]);

  // What the store draws: the page as it is here, with what was typed.
  const sent = useMemo(() => {
    if (!page) return null;
    const words = {};
    for (const [key, { text, field }] of texts) {
      const typed = drafts[key];
      const own = typed !== undefined ? unix(typed) : ownWords(text, language);
      const shown = own !== '' || field === 'shared' ? own || wordsOf(text, language) : other ? text.value : '';
      if (shown) words[key] = shown;
    }
    const shownPart = (entry) => ({
      id: entry.id, kind: entry.kind, anchor: entry.anchor, link: entry.link, photo: entry.photo, words: keysOf(entry.words),
    });
    return {
      page: {
        id: page.id, kind: page.kind, path: page.path, categoryId: page.categoryId, words: keysOf(page.words),
        sections: page.sections.filter((section) => section.isActive).map((section) => ({
          ...shownPart(section), style: section.style, link2: section.link2,
          items: section.items.filter((item) => item.isActive).map((item) => ({
            ...shownPart(item), icon: item.icon, categoryId: item.categoryId,
          })),
        })),
      },
      hub: {
        front: hub.front,
        categories: hub.categories.filter((category) => category.isActive)
          .map((category) => ({ id: category.id, name: wordsOf(category.name, language), colour: category.colour })),
        pages: hub.pages.map((entry) => ({ id: entry.id, path: entry.path })),
      },
      words,
    };
  }, [drafts, hub, language, other, page, texts]);

  // One change to what the page is made of: done at once, then read again.
  const act = useCallback(async (work, done) => {
    setBusy(true);
    setNotice(null);
    try {
      const result = await work();
      await one.refresh();
      onListChanged();
      if (done) done(result);
    } catch (error) {
      setNotice({ tone: 'bad', text: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  }, [one, onListChanged]);

  async function save() {
    setBusy(true);
    setNotice(null);
    try {
      await saveTexts(dirty.map((key) => [texts.get(key).text, unix(drafts[key])]),
        { language, contentTypeId: siteTextId, source: 'hub' });
      await one.refresh();
      onListChanged();
      setDrafts({});
      setNotice({ tone: 'ok', text: tx('ui.hub.save.saved') });
    } catch (error) {
      setNotice({ tone: 'bad', text: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  }

  // Brings a card, or a field in it, into view. Beside the page the cards scroll on
  // their own (hub.css), and only they move: the page stays where it is being read.
  const bring = useCallback((target, block) => {
    const box = cards.current;
    if (!box || !target) return;
    if (window.getComputedStyle(box).position !== 'sticky') {
      target.scrollIntoView({ block, behavior: 'smooth' });
      return;
    }
    const frame = box.getBoundingClientRect();
    const at = target.getBoundingClientRect();
    const top = at.top - frame.top + box.scrollTop;
    if (block === 'center') box.scrollTo({ top: top - (box.clientHeight - at.height) / 2, behavior: 'smooth' });
    else if (at.top < frame.top || at.height > box.clientHeight) box.scrollTo({ top, behavior: 'smooth' });
    else if (at.bottom > frame.bottom) box.scrollTo({ top: top + at.height - box.clientHeight, behavior: 'smooth' });
  }, []);
  // What the page beside the cards is asked to show. It is brought into view there
  // only while the cards stay beside it: on a narrow window they are over it, and
  // following the page would carry the card being written in out of sight.
  const show = useCallback((part) => setFocus({
    ...part, scroll: cards.current !== null && window.getComputedStyle(cards.current).position === 'sticky',
  }), []);
  const cardOf = (sectionId) => cards.current?.querySelector(`[data-section="${sectionId}"]`);
  const reveal = (sectionId) => {
    setOpenId(sectionId);
    window.requestAnimationFrame(() => bring(cardOf(sectionId), 'nearest'));
  };
  // A card opened among cards that scroll on their own is shown whole, as far as it fits.
  const settle = (sectionId) => window.requestAnimationFrame(() => {
    if (cards.current && window.getComputedStyle(cards.current).position === 'sticky') bring(cardOf(sectionId), 'nearest');
  });
  // A part of the page was pointed at: its card opens, and its field takes the cursor.
  const onPick = useCallback(({ field, section, item }) => {
    if (!page) return;
    const owner = page.sections.find((entry) => entry.id === section
      || entry.items.some((child) => child.id === item)
      || Object.values(entry.words).some((text) => text.key === field)
      || entry.items.some((child) => Object.values(child.words).some((text) => text.key === field)));
    if (!owner) return;
    setOpenId(owner.id);
    window.setTimeout(() => {
      const card = cards.current?.querySelector(`[data-section="${owner.id}"]`);
      const target = (field && card?.querySelector(`[data-key="${field}"] .xo-field`)) || (item && card?.querySelector(`[data-item="${item}"]`)) || card;
      bring(target, 'center');
      if (target?.matches?.('.xo-field')) target.focus({ preventScroll: true });
    }, 60);
  }, [bring, page]);

  if (one.error && !page) return <Failed message={one.error} onRetry={one.refresh} />;
  if (!page) return <Loading label={tx('ui.hub.loading')} />;

  const front = page.kind === 'front';
  const top = page.sections.find((section) => kinds.sections[section.kind]?.fixed);
  const missing = other ? [...texts.values()].filter(({ text, field }) => field !== 'shared' && text.value !== '' && ownWords(text, language) === '').length : 0;
  const editor = {
    language, kinds, hub, products, write, valueOf, type, act, setFocus: show, categories: hub.categories,
  };
  const order = page.sections.map((section) => section.id);
  const move = (index, by) => {
    const next = [...order];
    [next[index], next[index + by]] = [next[index + by], next[index]];
    act(() => setHubSectionOrder(page.id, next));
  };
  const present = new Set(page.sections.map((section) => section.kind));
  const addable = Object.entries(kinds.sections).filter(([, kind]) => kind.on === page.kind && !kind.fixed);

  return <div className="xo-hub-page">
    {other && <Alert tone="info">{tx('ui.hub.page.language_note', { language: languageLabel(languages.find((entry) => entry.code === language) || { name: language }) })}
      {missing > 0 ? ` ${tx('ui.hub.words.missing', { count: missing })}` : ''}</Alert>}
    {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}
    {undo && <Alert tone="info">{tx('ui.hub.section.removed')}{' '}
      <button type="button" className="xo-link" disabled={!write}
        onClick={() => act(() => (undo.item ? updateHubItem(undo.item, { isRemoved: false }) : updateHubSection(undo.section, { isRemoved: false })),
          () => setUndo(null))}>
        <Undo2 aria-hidden="true" />{tx('ui.hub.section.undo')}</button></Alert>}

    <div className="xo-hub-editor">
      <div className="xo-hub-cards" ref={cards}>
        {/* Over the sections: whether the page is shown on the store, and its address and category.
            The page's name is on the button that chose it, and its address over the preview. */}
        <div className="xo-hub-cards-head">
          <p className="xo-hub-side-label">{tx('ui.hub.sections.title')}</p>
          {!front && <div className="xo-hub-page-tools">
            <div className="xo-hub-publish">
              <span>{tx('ui.hub.page.publish')}</span>
              <Switch on={page.isPublished} disabled={!write} label={tx('ui.hub.page.publish')}
                onChange={(isPublished) => act(() => updateHubPage(page.id, { isPublished }))} />
            </div>
            <button type="button" className={`xo-btn-2${settings ? ' is-on' : ''}`} onClick={() => setSettings(!settings)}>
              <Settings aria-hidden="true" />{tx('ui.hub.page.settings')}</button>
          </div>}
        </div>
        {settings && !front && <PageSettings page={page} editor={editor} onClose={() => setSettings(false)} />}
        {page.sections.map((section, index) => <SectionCard key={section.id} section={section} editor={editor}
          open={openId === section.id}
          onToggle={() => {
            setOpenId(openId === section.id ? null : section.id);
            show({ section: section.id });
            if (openId !== section.id) settle(section.id);
          }}
          canUp={index > (top ? 1 : 0)} canDown={index < page.sections.length - 1 && section !== top}
          onUp={() => move(index, -1)} onDown={() => move(index, 1)}
          onRemoved={(removed) => setUndo(removed)} />)}

        {adding ? <div className="xo-hub-adding">
          <div className="xo-hub-adding-head">
            <strong>{tx('ui.hub.section.add_title')}</strong>
            <button type="button" className="xo-icon-btn" aria-label={tx('ui.hub.cancel')} onClick={() => setAdding(false)}><X aria-hidden="true" /></button>
          </div>
          <div className="xo-hub-kinds">
            {addable.map(([code, kind]) => {
              const Icon = KIND_ICON[code] || AlignLeft;
              const taken = kind.once && present.has(code);
              return <button key={code} type="button" disabled={!write || taken} title={tx(`ui.hub.kind.${code}.hint`)}
                onClick={() => act(() => createHubSection(page.id, { kind: code }), (created) => { setAdding(false); reveal(created.id); })}>
                <Icon aria-hidden="true" />
                <span><strong>{tx(`ui.hub.kind.${code}.name`)}</strong>
                  {taken && <small>{tx('ui.hub.section.already')}</small>}</span>
              </button>;
            })}
          </div>
        </div> : <button type="button" className="xo-btn xo-hub-add" disabled={!write} onClick={() => setAdding(true)}>
          <Plus aria-hidden="true" />{tx('ui.hub.section.add')}</button>}

        {dirty.length > 0 && <div className="xo-hub-save" role="status">
          <span>{tx('ui.hub.save.dirty', { count: dirty.length })}</span>
          <button type="button" className="xo-btn-2" disabled={busy} onClick={() => setDrafts({})}>{tx('ui.hub.save.discard')}</button>
          <button type="button" className="xo-btn" disabled={!write} onClick={save}>
            {busy ? tx('ui.hub.save.saving') : tx('ui.hub.save.save')}</button>
        </div>}
      </div>
      <HubPageLive kind={page.kind} sent={sent} focus={focus} onPick={onPick}
        address={`${SHOP_HOST}${page.path}`} href={page.isPublished ? `https://${SHOP_HOST}${page.path}` : ''} />
    </div>
  </div>;
}

export default React.forwardRef(HubPageEditor);

// The words of a part, as fields in the order XOmics lists them for its kind.
function Words({ owner, kind, of, editor, focusOn }) {
  const {
    language, valueOf, type, write, hub, products, setFocus,
  } = editor;
  return Object.entries(of.words).map(([field, input]) => {
    const text = owner.words[field];
    if (!text) return null;
    const shared = owner.defaults?.[field];
    const fallback = shared ? friendly(wordsOf(shared, language)) : '';
    return <div key={field} data-key={text.key}>
      <WordField kind={input} field={field} label={tx(`ui.hub.field.${kind}.${field}`)} text={text} language={language}
        value={valueOf(text, field)} fallback={fallback} disabled={!write} hub={hub} products={products}
        onChange={(value) => type(text, value)} onFocus={() => setFocus({ field: text.key, ...focusOn })} />
    </div>;
  });
}

// What a part is called in a list: the first of its words that is written.
function nameOf(owner, kind, editor) {
  for (const field of NAMED_BY[kind] || ['heading']) {
    const text = owner.words[field];
    if (!text) continue;
    const value = editor.valueOf(text, field) || friendly(wordsOf(text, editor.language))
      || (owner.defaults?.[field] ? friendly(wordsOf(owner.defaults[field], editor.language)) : '');
    if (value.trim() !== '') return clip(value.replace(/\s+/g, ' ').replace(/\*\*|^[•#→ ]+/g, ''));
  }
  return '';
}

// How much of a part is written in the language chosen, when it is not English.
function written(owner, editor) {
  if (editor.language === ENGLISH) return null;
  const all = [owner, ...(owner.items || [])].flatMap((part) => Object.values(part.words)).filter((text) => text.value !== '');
  if (all.length === 0) return null;
  const done = all.filter((text) => ownWords(text, editor.language) !== '').length;
  return done === all.length ? null : done === 0 ? 'none' : 'some';
}

export function Arrows({ canUp, canDown, disabled, onUp, onDown }) {
  return <span className="xo-hub-arrows">
    <button type="button" className="xo-icon-btn" disabled={disabled || !canUp} aria-label={tx('ui.hub.section.move_up')}
      title={tx('ui.hub.section.move_up')} onClick={onUp}><ArrowUp aria-hidden="true" /></button>
    <button type="button" className="xo-icon-btn" disabled={disabled || !canDown} aria-label={tx('ui.hub.section.move_down')}
      title={tx('ui.hub.section.move_down')} onClick={onDown}><ArrowDown aria-hidden="true" /></button>
  </span>;
}

// The line a card or an entry is in its list: its arrows, the button that
// opens it (what it is, and its first words), and its switch. The Quiz page's
// cards are the same lines.
export function Line({
  className, open, onToggle, arrows, picture, name, words, pills, shown,
}) {
  return <div className={className}>
    {arrows}
    <button type="button" className="xo-hub-toggle" aria-expanded={open} onClick={onToggle}>
      {picture}
      <span className="xo-hub-card-name"><strong>{name}</strong><small dir="auto">{words}</small></span>
      {pills}
      <ChevronDown aria-hidden="true" className="xo-hub-chevron" />
    </button>
    {shown}
  </div>;
}

function SectionCard({
  section, editor, open, onToggle, canUp, canDown, onUp, onDown, onRemoved,
}) {
  const { kinds, write, act, hub, products } = editor;
  const of = kinds.sections[section.kind] || { words: {} };
  const Icon = KIND_ICON[section.kind] || AlignLeft;
  const title = nameOf(section, section.kind, editor);
  const state = written(section, editor);
  const set = (changes) => act(() => updateHubSection(section.id, changes));
  const itemOrder = section.items.map((item) => item.id);
  const moveItem = (index, by) => {
    const next = [...itemOrder];
    [next[index], next[index + by]] = [next[index + by], next[index]];
    act(() => setHubItemOrder(section.id, next));
  };
  return <section className={`xo-hub-card${open ? ' is-open' : ''}${section.isActive ? '' : ' is-off'}`} data-section={section.id}
    aria-label={`${tx(`ui.hub.kind.${section.kind}.name`)}${title ? `: ${title}` : ''}`}>
    <Line className="xo-hub-card-head" open={open} onToggle={onToggle}
      arrows={of.fixed ? <span className="xo-hub-arrows is-none" aria-hidden="true" />
        : <Arrows canUp={canUp} canDown={canDown} disabled={!write} onUp={onUp} onDown={onDown} />}
      picture={<span className="xo-hub-card-icon"><Icon aria-hidden="true" /></span>}
      name={tx(`ui.hub.kind.${section.kind}.name`)} words={title}
      pills={<>
        {state && <Pill tone="warn">{state === 'none' ? tx('ui.hub.words.not_translated') : tx('ui.hub.words.partly')}</Pill>}
        {!section.isActive && <Pill tone="flat">{tx('ui.hub.section.off')}</Pill>}
      </>}
      shown={of.fixed ? <span className="xo-hub-shown is-none" aria-hidden="true" />
        : <Switch on={section.isActive} disabled={!write} label={tx('ui.hub.section.shown')} onChange={(isActive) => set({ isActive })} />} />
    {open && <div className="xo-hub-card-body">
      {of.photo && <PhotoField photo={section.photo} canFocus={CROPPED.has(section.kind)} disabled={!write} onSet={set} />}
      <Words owner={section} kind={section.kind} of={of} editor={editor} focusOn={{ section: section.id }} />
      {of.link && <LinkField label={tx('ui.hub.link.title')} value={section.link} hub={hub} products={products} disabled={!write}
        onChange={(link) => set({ link })} />}
      {of.link2 && <LinkField label={tx('ui.hub.link.title_2')} value={section.link2} hub={hub} products={products} disabled={!write}
        onChange={(link2) => set({ link2 })} />}
      {of.styles && <LookField value={section.style || of.styles[0]} looks={of.styles} disabled={!write} onChange={(style) => set({ style })} />}

      {of.items && <div className="xo-hub-items">
        {section.items.map((item, index) => <ItemRow key={item.id} item={item} section={section} editor={editor}
          canUp={index > 0} canDown={index < section.items.length - 1}
          onUp={() => moveItem(index, -1)} onDown={() => moveItem(index, 1)} onRemoved={onRemoved} />)}
        <div className="xo-hub-items-add">
          {of.items.map((kind) => <button key={kind} type="button" className="xo-btn-2" disabled={!write}
            onClick={() => act(() => createHubItem(section.id, kind))}>
            <Plus aria-hidden="true" />{tx(`ui.hub.item.${kind}.add`)}</button>)}
        </div>
      </div>}

      {!of.fixed && <div className="xo-hub-card-foot">
        <button type="button" className="xo-link is-danger" disabled={!write}
          onClick={() => act(() => updateHubSection(section.id, { isRemoved: true }), () => onRemoved({ section: section.id }))}>
          <Trash2 aria-hidden="true" />{tx('ui.hub.section.remove')}</button>
      </div>}
    </div>}
  </section>;
}

// One entry of a section: a line that opens on its fields.
function ItemRow({
  item, section, editor, canUp, canDown, onUp, onDown, onRemoved,
}) {
  const {
    kinds, write, act, hub, products, categories, setFocus,
  } = editor;
  const [open, setOpen] = useState(false);
  const of = kinds.items[item.kind] || { words: {} };
  const title = nameOf(item, item.kind, editor);
  const state = written(item, editor);
  const set = (changes) => act(() => updateHubItem(item.id, changes));
  const drawn = of.icon && ['tiles', 'rows'].includes(section.style);
  return <div className={`xo-hub-item${open ? ' is-open' : ''}${item.isActive ? '' : ' is-off'}`} data-item={item.id}>
    <Line className="xo-hub-item-head" open={open}
      onToggle={() => { setOpen(!open); setFocus({ item: item.id, section: section.id }); }}
      arrows={<Arrows canUp={canUp} canDown={canDown} disabled={!write} onUp={onUp} onDown={onDown} />}
      picture={item.photo ? <img className="xo-hub-item-thumb" src={item.photo.thumbnailUrl || item.photo.url} alt="" />
        : drawn ? <span className="xo-hub-item-thumb is-icon"><HubIcon name={item.icon} /></span> : null}
      name={tx(`ui.hub.item.${item.kind}.name`)} words={title || tx('ui.hub.section.empty')}
      pills={state && <Pill tone="warn">{state === 'none' ? tx('ui.hub.words.not_translated') : tx('ui.hub.words.partly')}</Pill>}
      shown={<Switch on={item.isActive} disabled={!write} label={tx('ui.hub.section.shown')} onChange={(isActive) => set({ isActive })} />} />
    {open && <div className="xo-hub-item-body">
      {of.photo && <PhotoField photo={item.photo} canFocus={CROPPED.has(item.kind)} disabled={!write} onSet={set} />}
      {of.category && <CategoryField value={item.categoryId} categories={categories} disabled={!write}
        onChange={(categoryId) => set({ categoryId })} />}
      <Words owner={item} kind={item.kind} of={of} editor={editor} focusOn={{ item: item.id, section: section.id }} />
      {drawn && <div className="xo-hub-link"><span className="xo-hub-label">{tx('ui.hub.icon.title')}</span>
        <IconField value={item.icon} disabled={!write} onChange={(icon) => set({ icon })} /></div>}
      {of.link && <LinkField label={tx('ui.hub.link.title')} value={item.link} hub={hub} products={products} disabled={!write}
        onChange={(link) => set({ link })} />}
      <div className="xo-hub-card-foot">
        <button type="button" className="xo-link is-danger" disabled={!write}
          onClick={() => act(() => updateHubItem(item.id, { isRemoved: true }), () => onRemoved({ item: item.id }))}>
          <Trash2 aria-hidden="true" />{tx('ui.hub.section.remove')}</button>
      </div>
    </div>}
  </div>;
}

// A guide's or an article's own settings: its address, its category and what
// search engines are told. The address and the category are saved here; the
// two texts are words like any other, saved with the bar at the bottom.
function PageSettings({ page, editor, onClose }) {
  const { act, write, categories, kinds } = editor;
  const start = page.path.replace(/^\/hub\//, '').replace(/\/$/, '');
  const [address, setAddress] = useState(start);
  const [categoryId, setCategoryId] = useState(page.categoryId);
  const changed = address !== start || categoryId !== page.categoryId;
  const of = { words: { meta_title: 'line', meta_description: 'text' } };
  void kinds;
  return <section className="xo-hub-settings">
    <div className="xo-hub-adding-head">
      <strong>{tx('ui.hub.page.settings')}</strong>
      <button type="button" className="xo-icon-btn" aria-label={tx('ui.hub.cancel')} onClick={onClose}><X aria-hidden="true" /></button>
    </div>
    <div className="xo-hub-settings-grid">
      <div className="xo-hub-field">
        <label htmlFor="xo-hub-address">{tx('ui.hub.settings.address')}</label>
        <div className="xo-hub-address-box"><span>{SHOP_HOST}/hub/</span>
          <input id="xo-hub-address" className="xo-field" type="text" value={address} disabled={!write} spellCheck={false}
            onChange={(event) => setAddress(event.target.value.toLowerCase().replace(/\s+/g, '-'))} /><span>/</span></div>
      </div>
      <CategoryField value={categoryId} categories={categories} disabled={!write} onChange={setCategoryId} />
    </div>
    <div className="xo-hub-card-foot">
      <button type="button" className="xo-btn" disabled={!write || !changed || address.trim() === ''}
        onClick={() => {
          if (page.isPublished && address !== start && !window.confirm(tx('ui.hub.settings.address_confirm'))) return;
          act(() => updateHubPage(page.id, { path: `/hub/${address.replace(/^\/+|\/+$/g, '')}/`, categoryId }));
        }}>{tx('ui.hub.settings.save')}</button>
    </div>
    <Words owner={page} kind="page" of={of} editor={editor} focusOn={{}} />
  </section>;
}
